import { DashboardEconomyScenarioResponse } from '@interfaces/dashboard/responses/dashboard-economy-scenario.response';

export type PerOperationItem = NonNullable<
  NonNullable<DashboardEconomyScenarioResponse['perOperation']>[number]
>;

/** Coluna ordenável da modal de operações (card 3323). */
export type OperationSortKey = 'operation' | 'initial' | 'final' | 'difference';
export type SortDirection = 'asc' | 'desc';

export interface OperationFilters {
  query: string;
  currencies: string[];
  banks: string[];
}

/** Minúsculas e sem acento, para a busca ignorar caixa e acentuação. */
export function normalizeSearchText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/** Prevista até Final − Prevista Inicial. */
export function operationDifference(op: PerOperationItem): number {
  return (op.economiaReal ?? 0) - (op.economiaPrevista ?? 0);
}

export function hasActiveFilters(filters: OperationFilters): boolean {
  return (
    normalizeSearchText(filters.query).length > 0 ||
    filters.currencies.length > 0 ||
    filters.banks.length > 0
  );
}

/** Busca por LOAN, banco e contrato; moeda e banco filtram pela lista escolhida (vazia = todas). */
export function filterOperations(
  operations: PerOperationItem[],
  filters: OperationFilters
): PerOperationItem[] {
  const query = normalizeSearchText(filters.query);
  const currencies = new Set(filters.currencies);
  const banks = new Set(filters.banks);

  return operations.filter(op => {
    if (currencies.size && !currencies.has(op.currencyCode)) return false;
    if (banks.size && !banks.has(op.bankName ?? '')) return false;
    if (!query) return true;
    const haystack = normalizeSearchText([op.loanCode, op.bankName, op.reference].join(' '));
    return haystack.includes(query);
  });
}

function sortValue(op: PerOperationItem, key: OperationSortKey): number {
  switch (key) {
    case 'initial':
      return op.economiaPrevista ?? 0;
    case 'final':
      return op.economiaReal ?? 0;
    default:
      return operationDifference(op);
  }
}

export function sortOperations(
  operations: PerOperationItem[],
  key: OperationSortKey,
  direction: SortDirection
): PerOperationItem[] {
  const factor = direction === 'asc' ? 1 : -1;
  return [...operations].sort((a, b) => {
    if (key === 'operation') {
      return (
        factor *
        (a.loanCode ?? '').localeCompare(b.loanCode ?? '', 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        })
      );
    }
    return factor * (sortValue(a, key) - sortValue(b, key));
  });
}

/** Valores distintos e não vazios, em ordem alfabética. */
export function distinctSorted(values: (string | null | undefined)[]): string[] {
  return [...new Set(values.filter((v): v is string => !!v && v.trim().length > 0))].sort((a, b) =>
    a.localeCompare(b, 'pt-BR')
  );
}
